import { useEffect, useRef, useState, type ReactNode } from 'react';
import axios from 'axios';
import { Loader2, CloudUpload, Check, Clock, MonitorPlay, Trash2 } from 'lucide-react';
import { currentRun, deleteRun, docRuns, showRun, type DocRun } from './docState';
import { PopMenu } from './PopMenu';
import { NavArrow } from './NavArrow';
import { usePublishStatus } from './publishStatus';
import { useLanguage } from '../../contexts/LanguageContext';
import { IS_DEMO, IS_LOCKED } from '../../apiBase';

// Browse the document's runs.
//
// A run used to be a version of the page CLASSIFICATION — the only history in the app,
// paged from under the Phase 1 table, with the other five phases keeping one answer
// each that a re-run destroyed. A run is a version of the whole DOCUMENT now, so this
// pages the manual, and every phase follows it.
//
// TWO sliders, split by origin. A run's number (`seq`) is now assigned within its
// origin — dev-authored runs count 1, 2, 3… independently of prod-authored ones — and
// is never renumbered across a prod⇄dev sync. So PROD run 22 is the same run, with the
// same number, in both apps; likewise DEV run 4. One merged slider used to renumber
// them together on every pull, which is why the same run showed different numbers on
// prod and dev. Each slider pages its own origin; the current run lives in one of them.
//
// It reads docState directly rather than taking props. There is no store to subscribe
// to — the runs are a fact about the document, not about a phase — and the only thing
// that moves them is the button below, which re-renders on its own state.

// ONE ROW OF THE RUN'S MENU, centred like everything else in it.
const ROW =
  'flex w-full items-center justify-center gap-2 whitespace-nowrap px-4 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800';

// A ROW THAT ASKS FIRST. Pressed, it becomes its question: the action in its colour, and a
// way out beside it. In place, in the menu, where the thing it acts on is named — no dialog
// over a menu over a pager. Everything these do writes somewhere other people look.
function ConfirmRow({ icon, label, confirmLabel, tone, busy, error, note, onConfirm }: {
  icon: ReactNode;
  label: string;
  confirmLabel: string;
  tone: 'blue' | 'sky' | 'rose';
  busy: boolean;
  error: string;
  // Said while armed, above the two buttons, when there is something worth saying.
  note?: ReactNode;
  onConfirm: () => void;
}) {
  const { t } = useLanguage();
  const [armed, setArmed] = useState(false);
  const fill = { blue: 'bg-blue-600 hover:bg-blue-700', sky: 'bg-sky-600 hover:bg-sky-700', rose: 'bg-rose-600 hover:bg-rose-500' }[tone];
  if (!armed && !busy) {
    return (
      <button
        type="button"
        onClick={() => setArmed(true)}
        title={error || undefined}
        className={`${ROW} ${error ? 'text-rose-600 dark:text-rose-400' : ''}`}
      >
        {icon}
        {label}
      </button>
    );
  }
  return (
    <div className="px-3 py-1.5">
      {note}
      <div className="flex items-center justify-center gap-1.5">
        <button
          type="button"
          onClick={() => {
            setArmed(false);
            onConfirm();
          }}
          disabled={busy}
          className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-50 ${fill}`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
          {confirmLabel}
        </button>
        {!busy && (
          <button
            type="button"
            onClick={() => setArmed(false)}
            className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            {t('notice.catalog.cancel')}
          </button>
        )}
      </div>
    </div>
  );
}

const errorOf = (e: unknown, fallback: string) =>
  axios.isAxiosError(e) && e.response?.data?.error ? e.response.data.error : fallback;

// SEND THIS RUN SOMEWHERE ELSE — its manual, the run row and every answer.
//
// To PROD, from dev (DEV-ONLY, import.meta.env.DEV), where it lands marked DEV.
//
// To BOTH COPIES, the demo and the sandbox, with the notice's photographs too. They are
// separate databases and nothing crosses into either on its own: this is the crossing. One
// row for the two of them: they hold the same data and differ only in what they may do with
// it, and the run gets ONE number across both, so a run is run 4 on the demo and run 4 on the
// sandbox. The parts database is re-mirrored into both in the same call — a run whose parts a
// copy has never heard of would open as a page of missing pictures. Not in either copy, where
// it would be a copy sending a run to itself.
function SendRunRow({ docId, runId, url, icon, label, done, fallback, tone }: {
  docId: string;
  runId: string;
  url: string;
  icon: ReactNode;
  label: string;
  done: string;
  fallback: string;
  tone: 'blue' | 'sky';
}) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  if (sent) {
    return (
      <p className={`${ROW} font-semibold hover:bg-transparent ${tone === 'sky' ? 'text-sky-600 dark:text-sky-400' : 'text-blue-600 dark:text-blue-400'}`}>
        <Check className="h-3.5 w-3.5" /> {done}
      </p>
    );
  }
  return (
    <ConfirmRow
      icon={icon}
      label={error ? `${label} — retry` : label}
      confirmLabel={label}
      tone={tone}
      busy={busy}
      error={error}
      onConfirm={async () => {
        setBusy(true);
        setError('');
        try {
          await axios.post(url, { docId, runId });
          setSent(true);
        } catch (e) {
          setError(errorOf(e, fallback));
        }
        setBusy(false);
      }}
    />
  );
}

// Delete the run on screen, from its menu.
//
// THE PUBLISHED RUN says so while armed — that run and no other. A tutorial is a snapshot
// of one version of the document, so warning on every run of a published manual was a
// warning about a page eleven of the twelve had nothing to do with, and a warning that
// cries wolf is read once. Nothing on the live page breaks either way: it is a snapshot,
// and the videos it plays are not run-scoped. It is worth knowing, not worth stopping for.
//
// A page published before the run was recorded reports none, and none is UNKNOWN rather
// than "all of them" — so it warns about nothing.
function DeleteRunRow({ docId, runId, seq }: { docId: string; runId: string; seq: number }) {
  const { t } = useLanguage();
  const publish = usePublishStatus(docId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return (
    <ConfirmRow
      icon={<Trash2 className="h-3.5 w-3.5" />}
      label={t('notice.run.delete')}
      confirmLabel={t('notice.run.deleteN').replace('{n}', String(seq))}
      tone="rose"
      busy={busy}
      error={error}
      note={publish.published && publish.runId === runId && (
        <p className="mb-1.5 max-w-[16rem] whitespace-normal text-center text-xs text-amber-600 dark:text-amber-400">
          {t('notice.run.deletePublished')}
        </p>
      )}
      onConfirm={async () => {
        setBusy(true);
        setError('');
        try {
          await deleteRun(docId, runId);
        } catch (e) {
          setError(errorOf(e, t('notice.run.deleteFailed')));
          setBusy(false);
        }
      }}
    />
  );
}

// The run's creation instant, in Europe/Paris time. '' when the run carries no timestamp
// (an older row from before the field existed).
function useRunWhen(run: DocRun): string {
  const { language } = useLanguage();
  if (!run.createdAt) return '';
  const formatted = new Intl.DateTimeFormat(language === 'fr' ? 'fr-FR' : 'en-GB', {
    timeZone: 'Europe/Paris',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(run.createdAt));
  // Only the FIRST letter, in JS — CSS `capitalize` would uppercase every word, turning
  // the French "à" into "À" and "juillet" into "Juillet". Correct French keeps both low.
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

// EVERYTHING ABOUT THE RUN ON SCREEN, BEHIND ONE BUTTON: when it was made, and what can be
// done with it — delete it, send it to the copies, push it to prod. They were three and four
// controls hung off the pager's edge, which put a row of buttons beside a pager whose job is
// paging. Keyed by run in the parent, so paging closes it and resets whatever was armed.
//
// The button IS the counter: "Run 21/21" opens it. A gear beside the counter was a second
// thing to aim at for the one run the counter already names.
function RunMenu({ docId, run, disabled, canDelete, canPush, canSend, children }: {
  docId: string;
  run: DocRun;
  // While a run is going, like the pager's arrows: nothing is deleted or sent mid-run.
  disabled?: boolean;
  canDelete: boolean;
  canPush: boolean;
  canSend: boolean;
  // The counter, drawn as the button.
  children: ReactNode;
}) {
  const { t } = useLanguage();
  const when = useRunWhen(run);
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  // Nothing to show: the counter, and no menu behind it.
  if (!when && !canDelete && !canPush && !canSend) return <span className={COUNTER}>{children}</span>;
  const title = `${t('notice.cat.run')} ${run.seq}`;
  return (
    <>
      <button
        ref={anchor}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        title={title}
        className={COUNTER}
      >
        {children}
      </button>
      {open && (
        <PopMenu anchor={anchor} onClose={() => setOpen(false)}>
          <p className="px-4 pb-1 pt-1.5 text-center text-[10px] font-bold uppercase tracking-wider text-slate-900 dark:text-slate-100">
            {title}
          </p>
          {when && (
            <p className="flex items-center justify-center gap-2 px-4 pb-2 text-xs text-slate-500 dark:text-slate-400">
              <Clock className="h-3.5 w-3.5" />
              {when}
            </p>
          )}
          {(canDelete || canPush || canSend) && (
            <div className="border-t border-slate-100 pt-1 dark:border-slate-800">
              {canPush && (
                <SendRunRow
                  docId={docId}
                  runId={run.id}
                  url="/api/notice/admin/sync/push-run"
                  icon={<CloudUpload className="h-3.5 w-3.5" />}
                  label="Push to prod"
                  done="Pushed to prod"
                  fallback="Push failed — is the tunnel up?"
                  tone="blue"
                />
              )}
              {canSend && (
                <SendRunRow
                  docId={docId}
                  runId={run.id}
                  url="/api/notice/copies/push-run"
                  icon={<MonitorPlay className="h-3.5 w-3.5" />}
                  label="Send to demo & sandbox"
                  done="Sent"
                  fallback="Send failed."
                  tone="sky"
                />
              )}
              {canDelete && <DeleteRunRow docId={docId} runId={run.id} seq={run.seq} />}
            </div>
          )}
        </PopMenu>
      )}
    </>
  );
}

// One origin's pager. `runs` are that origin's runs, ascending by seq. When the
// document's current run is one of them, the arrows page within the group and the
// pager reads bright; when it is not, the pager reads dimmed and either arrow jumps
// into this origin at its latest run — i.e. clicking a dimmed slider switches origin.
function GroupPager({
  label,
  fromDev,
  runs,
  currentId,
  loading,
  disabled,
  onGo,
  menu,
}: {
  label: string;
  fromDev: boolean;
  runs: DocRun[];
  currentId: string;
  // The spinner belongs to the group we're navigating TO (which holds the pending run),
  // not the one we're leaving — the parent decides that and passes it here.
  loading: boolean;
  disabled?: boolean;
  onGo: (runId: string) => void;
  // The run's menu, given the counter to wear as its button — on the row holding the loaded
  // run. Without one the counter is what it was: a click on a dimmed row switches origin.
  menu?: (counter: ReactNode) => ReactNode;
}) {
  const { t } = useLanguage();
  const last = runs.length - 1;
  const curIdx = runs.findIndex((r) => r.id === currentId);
  const active = curIdx >= 0;
  const shown = runs[active ? curIdx : last];

  const goPrev = () => (!active ? onGo(runs[last].id) : curIdx > 0 && onGo(runs[curIdx - 1].id));
  const goNext = () => (!active ? onGo(runs[last].id) : curIdx < last && onGo(runs[curIdx + 1].id));
  // `busy` is NOT a disable condition: it flips true→false within one navigation, and
  // gating the buttons on it flashed all four to their disabled style and back on every
  // click. Re-entry is already prevented in `go()`; the counter's spinner is the feedback.
  const prevDisabled = disabled || (active && curIdx <= 0);
  const nextDisabled = disabled || (active && curIdx >= last);

  const badge =
    'rounded px-1.5 py-px text-[10px] font-semibold uppercase leading-none tracking-wide ' +
    (fromDev
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
      : 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300');

  const counter = (
    <>
      {/* The spinner while a run loads, and nothing otherwise: the number says what it is. */}
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {t('notice.cat.run')} {shown.seq}/{runs[last].seq}
    </>
  );

  // The origin heads the row, on its own line — the rail is 200px inside, and a badge beside
  // the arrows does not fit. The arrows take the full width under it.
  return (
    <div className={'flex flex-col gap-1.5 ' + (active ? '' : 'opacity-60')}>
      {/* An empty label draws nothing rather than an empty line. */}
      {label && <span className={badge + ' self-start'}>{label}</span>}
      <div className="flex items-center justify-between gap-1.5">
        <NavArrow dir="prev" onClick={goPrev} disabled={prevDisabled} label={t('notice.cat.prevRun')} />
        {menu ? menu(counter) : (
          <button
            type="button"
            onClick={() => onGo(shown.id)}
            disabled={disabled}
            title={`${t('notice.cat.run')} ${shown.seq}`}
            className={COUNTER}
          >
            {counter}
          </button>
        )}
        <NavArrow dir="next" onClick={goNext} disabled={nextDisabled} label={t('notice.cat.nextRun')} />
      </div>
    </div>
  );
}

// The run counter, "Run 21/21" — a button either way: the run's menu on the loaded run's
// row, a switch of origin on a dimmed one.
const COUNTER =
  'flex items-center justify-center gap-1.5 rounded-lg px-1 py-1 text-sm font-semibold tabular-nums text-white transition-colors hover:text-blue-200 disabled:cursor-default disabled:hover:text-white';

// How long paging waits after the last click before it actually fetches — long enough
// that stepping through a run of clicks fetches only the one you land on, short enough
// that a single click still feels immediate.
const NAV_DEBOUNCE_MS = 220;

export function RunNav({ docId, disabled }: { docId: string; disabled?: boolean }) {
  // The run we are heading TO while a load settles; null when at rest on the loaded run.
  // Paging sets it instantly, so the counter tracks the clicks even though only the run
  // you land on is fetched — stepping back ten runs no longer loads the nine in between.
  const [target, setTarget] = useState<string | null>(null);
  // Aborts the in-flight load (a click mid-load supersedes it at once) and the debounce
  // timer (a burst of clicks collapses to one fetch).
  const loadCtl = useRef<AbortController | null>(null);
  const loadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runs = docRuns(docId);
  const curId = currentRun(docId);

  // A landed load makes the loaded run the shown one, so drop the target: display and
  // truth are the same again. Fires on every curId change — including a fresh run opened
  // elsewhere — so a stale target can never outlive the run it pointed past.
  useEffect(() => {
    setTarget(null);
  }, [curId]);

  // If the pager unmounts mid-navigation, don't leave a timer or a fetch running.
  useEffect(
    () => () => {
      if (loadTimer.current) clearTimeout(loadTimer.current);
      loadCtl.current?.abort();
    },
    [],
  );

  if (!runs.length || !curId) return null;

  // The run the pager DISPLAYS: where we're heading if navigating, else the loaded run.
  const shownId = target && runs.some((r) => r.id === target) ? target : curId;
  const navigating = shownId !== curId;

  const prodRuns = runs.filter((r) => !r.fromDev).sort((a, b) => a.seq - b.seq);
  const devRuns = runs.filter((r) => r.fromDev).sort((a, b) => a.seq - b.seq);
  const cur = runs.find((r) => r.id === curId);

  // Show a pager when its origin holds real history (≥2 runs), or whenever BOTH
  // origins are present — then even a lone run on one side is worth labelling, so you
  // can see it and cross over to it. The push button appears in dev, for a dev run.
  const bothOrigins = prodRuns.length > 0 && devRuns.length > 0;
  const showPush = import.meta.env.DEV && !!cur?.fromDev;
  // Every run can be sent, whichever origin made it — and only from PROD, which is the only
  // app that has copies. On either copy this is a button pointing at itself: the demo has
  // nowhere to send one to, and the sandbox pressing it would be a copy writing the copies,
  // from its own database, over the data it was given.
  //
  // The server refuses it on both already — the route opens with refuse_spend and both copies
  // set NOTICE_NO_SPEND — so this was a button that reached the server and came back refused.
  // Off the page rather than greyed, unlike the run buttons: those say what the app does and
  // that this copy will not do it, and this one describes a relationship a copy is not in.
  //
  // IS_LOCKED and not IS_DEMO, because the two copies are exactly the builds that may not
  // press. See apiBase.
  const showSendCopies = !IS_LOCKED;

  // A COPY HAS ONE SLIDER AND ALWAYS SHOWS IT.
  //
  // One, because it has one origin: everything over there arrived from prod, filed
  // under the copy's own numbering from 1 (see push_run_to, which files every run
  // from_dev FALSE whichever side of prod's two made it), so a second row labelled DEV
  // would be a row that can never have anything in it.
  //
  // Always, because the threshold here is about clutter and a copy's case is the
  // opposite one. Two runs are the point at which a pager earns its place in a working
  // app; on a copy a single run is the whole history, and "Run 1/1" says that this is
  // one pass of several the app can hold — which is exactly what a copy is shown for.
  //
  // IS_LOCKED and not IS_DEMO, because the SANDBOX is a copy in every way this asks
  // about: one origin, its own numbering, and nothing of its own to compare against.
  //
  // The DEMO is the exception, and gets no pager at all. It is the one copy shown to
  // somebody who is not driving it: "Run 1/1" invites a click that changes the whole
  // document underneath them, to reach a run that does not exist. The sandbox keeps
  // its pager — it is worked in, not shown.
  const showProd = IS_DEMO
    ? false
    : IS_LOCKED
      ? prodRuns.length >= 1
      : prodRuns.length >= 2 || (bothOrigins && prodRuns.length >= 1);
  // The DEV row also appears whenever there's a run to push, so the push button —
  // which rides on that row — always has a home.
  const showDev = !IS_LOCKED
    && (devRuns.length >= 2 || (bothOrigins && devRuns.length >= 1) || showPush);
  if (!showProd && !showDev) return null;

  // Fetch the run we've settled on. Aborts any load already running first, so the newest
  // selection always wins; every phase's stores re-read inside showRun — it is the whole
  // document changing, not a number.
  const load = async (runId: string) => {
    loadCtl.current?.abort();
    const ctl = new AbortController();
    loadCtl.current = ctl;
    await showRun(docId, runId, ctl.signal);
    if (ctl.signal.aborted) return; // superseded — leave target where the newer click put it
    loadCtl.current = null;
    // Arrived: curId is now runId. Clear the target — this also re-renders the pager onto
    // the freshly-loaded run (nothing else here subscribes to the run changing).
    setTarget(null);
  };

  // A click moves the counter NOW and schedules the fetch for once the clicks stop —
  // aborting any load already in flight, so mid-load you can keep paging and it stops
  // right away rather than making you wait it out.
  const go = (runId: string) => {
    if (runId === shownId) return;
    loadCtl.current?.abort();
    if (loadTimer.current) clearTimeout(loadTimer.current);
    setTarget(runId === curId ? null : runId);
    if (runId === curId) return; // landed back on the loaded run — nothing to fetch
    loadTimer.current = setTimeout(() => void load(runId), NAV_DEBOUNCE_MS);
  };

  // IN THE SIDEBAR, under the navigation and above the submodel (see DocSidebar): it chooses
  // which version of the whole document is on screen, so it sits with the other controls
  // about the whole document, on every phase — no longer over Phase 1 alone.
  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-slate-600 pt-4">
      {showProd && (
        <GroupPager
          // Labelled only beside a DEV row, where there is something to tell it from: alone,
          // every run is prod's, and on a copy a row reading PROD is the copy claiming to be
          // the app (showDev is never true on one).
          label={showDev ? 'PROD' : ''}
          fromDev={false}
          runs={prodRuns}
          currentId={shownId}
          loading={navigating && prodRuns.some((r) => r.id === shownId)}
          disabled={disabled}
          onGo={go}
          // The run's menu rides the row that holds the loaded run — here, when it's a prod
          // run. Delete is left out when the document has one run left: the server refuses
          // it, and a control that can only fail is not a control.
          menu={
            cur && !cur.fromDev ? (counter) => (
              <RunMenu
                key={cur.id}
                docId={docId}
                run={cur}
                disabled={disabled}
                canDelete={!IS_DEMO && runs.length > 1}
                canPush={false}
                canSend={showSendCopies}
              >
                {counter}
              </RunMenu>
            ) : undefined
          }
        />
      )}
      {showDev && (
        <GroupPager
          label="DEV"
          fromDev
          runs={devRuns}
          currentId={shownId}
          loading={navigating && devRuns.some((r) => r.id === shownId)}
          disabled={disabled}
          onGo={go}
          // The run's menu when the loaded run is a dev run, and the push on any run in dev.
          menu={
            cur && (cur.fromDev || showPush) ? (counter) => (
              <RunMenu
                key={`dev-${cur.id}`}
                docId={docId}
                run={cur}
                disabled={disabled}
                canDelete={cur.fromDev && runs.length > 1}
                canPush={showPush}
                canSend={showSendCopies && cur.fromDev}
              >
                {counter}
              </RunMenu>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
